import React, { ReactNode } from 'react';
import {
  Box,
  Checkbox,
  Divider,
  InputLabel,
  TextField,
  Typography
} from '@p81-common/ui';
import {
  CredentialsFields,
  CredentialsFieldLabels
} from '../CredentialsFields';
import { isUuidValid } from '../CredentialsFields/helpers';
import { WarningAttentionBox } from '../ui';
import { useTouchedFields } from '../hooks';
import { CredentialsField, CredentialsFormValues } from './helpers';
import { createClasses } from './styles';

export interface CredentialsStepLabels {
  sectionTitle?: string;
  /**
   * Renders an attention box above the fields. The MSSP Parent semi-migration
   * carries its "detected" callout here because its mode-select screen has none
   * (Figma 764:1543); the MSSP Child flows put theirs on the mode select instead.
   */
  warningTitle?: string;
  parentUuidLabel: string;
  parentUuidPlaceholder: string;
  parentUuidError: string;
  parentUuidHint: string;
  uuidConfirmationCheckbox: string;
  fields: CredentialsFieldLabels;
}

interface CredentialsStepProps {
  values: CredentialsFormValues;
  onChange: (next: CredentialsFormValues) => void;
  labels: CredentialsStepLabels;
  children?: ReactNode;
}

const CredentialsStep = ({
  values,
  onChange,
  labels,
  children
}: CredentialsStepProps) => {
  const classes = createClasses();

  const { touched, markTouched } = useTouchedFields<CredentialsField>({
    parentUuid: !!values.parentUuid,
    clientId: !!values.clientId,
    secretKey: !!values.secretKey,
    adminEmail: !!values.adminEmail
  });

  const update = (patch: Partial<CredentialsFormValues>) =>
    onChange({ ...values, ...patch });

  const parentUuidError = touched.parentUuid && !isUuidValid(values.parentUuid);

  return (
    <Box className={classes.container}>
      {labels.warningTitle && (
        <WarningAttentionBox title={labels.warningTitle} />
      )}
      {labels.sectionTitle && (
        <Typography variant="subtitle1" className={classes.sectionTitle}>
          {labels.sectionTitle}
        </Typography>
      )}
      <Box>
        <TextField
          value={values.parentUuid}
          label={labels.parentUuidLabel}
          required
          placeholder={labels.parentUuidPlaceholder}
          onChange={e =>
            update({ parentUuid: e.target.value, uuidConfirmed: false })
          }
          onBlur={() => markTouched('parentUuid')}
          error={parentUuidError}
          helperText={parentUuidError ? labels.parentUuidError : ''}
          fullWidth
        />
        <Typography variant="caption" className={classes.uuidHint}>
          {labels.parentUuidHint}
        </Typography>
      </Box>

      <InputLabel className={classes.confirmationLabel}>
        <Checkbox
          checked={values.uuidConfirmed}
          onChange={e => update({ uuidConfirmed: e.target.checked })}
        />
        <Typography variant="body2">
          {labels.uuidConfirmationCheckbox}
        </Typography>
      </InputLabel>

      <Divider />

      <CredentialsFields
        values={{
          clientId: values.clientId,
          secretKey: values.secretKey,
          adminEmail: values.adminEmail
        }}
        onChange={update}
        touched={touched}
        onBlur={markTouched}
        labels={labels.fields}
      />

      {children}
    </Box>
  );
};

export default CredentialsStep;
